import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { CheckIcon, ClipboardDocumentIcon } from "@heroicons/react/24/outline";

import Header from "@/components/Header";
import EpisodeCard from "@/components/EpisodeCard";
import EpisodeModal from "@/components/EpisodeModal";
import { usePodcast } from "@/contexts/PodcastContext";
import { apiClient } from "@/api/client";
import { useEpisodes, type Episode } from "@/hooks/useEpisodes";
import { useCredits } from "@/hooks/useCredits";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  responseId?: string;
  verdict?: "answer" | "insufficient";
  usedContext?: "current" | "previous" | "both";
  missing?: string[];
  chunksUsed?: number;
  model?: string;
  referencedEpisodeNumbers?: number[];
}

const formatUsedContext = (value?: ChatMessage["usedContext"]) => {
  switch (value) {
    case "current":
      return "Current context";
    case "previous":
      return "Previous reply context";
    case "both":
      return "Blended context";
    default:
      return undefined;
  }
};

const formatMessageForCopy = (message: ChatMessage) => {
  const sections: string[] = [];
  const body = (message.content ?? "").trimEnd();

  if (body) {
    sections.push(body);
  }

  const meta: string[] = [];

  if (message.role === "assistant" && message.verdict) {
    const verdictLabel = message.verdict === "answer" ? "Answer provided" : "Insufficient context";
    meta.push(`Verdict: ${verdictLabel}`);

    const used = formatUsedContext(message.usedContext);
    if (used) {
      meta.push(`Context: ${used}`);
    }

    if (typeof message.chunksUsed === "number") {
      meta.push(`Chunks used: ${message.chunksUsed}`);
    }

    if (message.model) {
      meta.push(`Model: ${message.model}`);
    }
  }

  if (message.missing?.length) {
    meta.push(`Missing information:\n${message.missing.map(item => `- ${item}`).join("\n")}`);
  }

  if (meta.length) {
    sections.push(meta.join("\n"));
  }

  return sections.join("\n\n---\n\n") || body;
};

export default function ChatPage() {
  const { selectedPodcast, availablePodcasts, isLoading: podcastLoading } = usePodcast();
  const { episodes, isLoading: episodesLoading } = useEpisodes(selectedPodcast?.id);
  const { refreshCredits } = useCredits();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastResponseId, setLastResponseId] = useState<string | undefined>(undefined);
  const [copiedChat, setCopiedChat] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [episodeReferenceCounts, setEpisodeReferenceCounts] = useState<Record<number, number>>({});
  const [selectedEpisode, setSelectedEpisode] = useState<Episode | null>(null);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMessages([]);
    setInputMessage("");
    setError("");
    setLastResponseId(undefined);
    setEpisodeReferenceCounts({});
    setSelectedEpisode(null);
  }, [selectedPodcast?.id]);

  useEffect(() => {
    if (messagesEndRef.current && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    if (!selectedPodcast) {
      setError("Please select a podcast first.");
      return;
    }

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: inputMessage,
      timestamp: Date.now(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);
    setError("");

    try {
      const response = await apiClient.askPodcast(selectedPodcast.id, {
        question: inputMessage,
        previous_response_id: lastResponseId,
        k: 12,
        max_context_tokens: 12000,
        score_threshold: 0.4,
      });

      setLastResponseId(response.response_id);

      const uniqueReferencedNumbers = Array.from(
        new Set(
          (response.referenced_episode_numbers ?? []).filter(
            (value): value is number => typeof value === "number" && Number.isFinite(value),
          ),
        ),
      );
      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: response.answer,
        timestamp: Date.now(),
        responseId: response.response_id,
        verdict: response.verdict,
        usedContext: response.used_context,
        missing: response.missing ?? [],
        chunksUsed: response.chunks_used,
        model: response.model,
        referencedEpisodeNumbers: uniqueReferencedNumbers,
      };

      if (uniqueReferencedNumbers.length) {
        setEpisodeReferenceCounts(prevCounts => {
          let updated = false;
          const nextCounts = { ...prevCounts };
          uniqueReferencedNumbers.forEach(value => {
            updated = true;
            nextCounts[value] = (nextCounts[value] ?? 0) + 1;
          });
          return updated ? nextCounts : prevCounts;
        });
      }

      setMessages(prev => [...prev, assistantMessage]);

      // Refresh credits since /ask consumes credits
      void refreshCredits();
    } catch (err) {
      console.error("Error asking question", err);
      const details = err instanceof Error ? err.message : "Unknown error";
      setError(`An error occurred: ${details}`);

      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `I'm sorry, I encountered an error: ${details}. Please make sure the podcast has been indexed for RAG.`,
        timestamp: Date.now(),
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setLastResponseId(undefined);
    setEpisodeReferenceCounts({});
    setSelectedEpisode(null);
  };

  const episodesByNumber = useMemo(() => {
    const map = new Map<number, Episode>();
    episodes.forEach(episode => {
      if (typeof episode.episodeNumber === "number" && Number.isFinite(episode.episodeNumber)) {
        map.set(episode.episodeNumber, episode);
      }
    });
    return map;
  }, [episodes]);

  const referencedEpisodes = useMemo(() => {
    const entries = Object.entries(episodeReferenceCounts);
    if (entries.length === 0) {
      return [];
    }

    const collected: Array<{ episode: Episode; count: number; publishedAt: number }> = [];
    entries.forEach(([rawKey, count]) => {
      const episodeNumber = Number(rawKey);
      if (!Number.isFinite(episodeNumber)) {
        return;
      }
      const episode = episodesByNumber.get(episodeNumber);
      if (!episode) {
        return;
      }
      const publishedAt = new Date(episode.publishedDate ?? 0).getTime();
      collected.push({
        episode,
        count,
        publishedAt: Number.isFinite(publishedAt) ? publishedAt : 0,
      });
    });

    collected.sort((left, right) => {
      if (right.count !== left.count) {
        return right.count - left.count;
      }
      if (right.publishedAt !== left.publishedAt) {
        return right.publishedAt - left.publishedAt;
      }
      return left.episode.title.localeCompare(right.episode.title);
    });

    return collected;
  }, [episodeReferenceCounts, episodesByNumber]);

const markdownComponents = useMemo(() => {
    const baseClass = "text-gray-900 dark:text-gray-100";
    return {
      p: ({ children, ...props }: any) => (
        <p className={`mb-4 leading-relaxed ${baseClass}`} {...props}>
          {children}
        </p>
      ),
      h1: ({ children, ...props }: any) => (
        <h1 className={`mt-6 mb-4 text-2xl font-semibold ${baseClass}`} {...props}>
          {children}
        </h1>
      ),
      h2: ({ children, ...props }: any) => (
        <h2 className={`mt-5 mb-3 text-xl font-semibold ${baseClass}`} {...props}>
          {children}
        </h2>
      ),
      h3: ({ children, ...props }: any) => (
        <h3 className={`mt-4 mb-2 text-lg font-semibold ${baseClass}`} {...props}>
          {children}
        </h3>
      ),
      a: ({ children, ...props }: any) => (
        <a
          className="font-semibold text-gray-700 underline decoration-2 underline-offset-4 hover:text-gray-900 dark:text-amber-400 dark:hover:text-amber-200"
          target="_blank"
          rel="noreferrer"
          {...props}
        >
          {children}
        </a>
      ),
      ul: ({ children, ...props }: any) => (
        <ul className={`mb-4 list-disc space-y-2 pl-6 ${baseClass}`} {...props}>
          {children}
        </ul>
      ),
      ol: ({ children, ...props }: any) => (
        <ol className={`mb-4 list-decimal space-y-2 pl-6 ${baseClass}`} {...props}>
          {children}
        </ol>
      ),
      li: ({ children, ...props }: any) => (
        <li className="leading-relaxed" {...props}>
          {children}
        </li>
      ),
      blockquote: ({ children, ...props }: any) => (
        <blockquote
          className="my-4 border-l-4 border-gray-300 bg-gray-100/60 pl-4 italic text-gray-700 dark:border-gray-600 dark:bg-gray-800/60 dark:text-gray-200"
          {...props}
        >
          {children}
        </blockquote>
      ),
      code: ({ inline, className, children, ...props }: any) => {
        const codeContent = Array.isArray(children) ? children.join("") : String(children ?? "");
        if (inline) {
          return (
            <code
              className="rounded-md bg-gray-200 px-1.5 py-0.5 font-mono text-sm text-gray-800 dark:bg-gray-800 dark:text-gray-100"
              {...props}
            >
              {codeContent}
            </code>
          );
        }
        return (
          <pre className="my-5 overflow-x-auto rounded-xl border border-gray-200 bg-gray-900 p-4 text-sm text-gray-100 dark:border-gray-700">
            <code className={`font-mono ${className ?? ""}`} {...props}>
              {codeContent.replace(/\n$/, "")}
            </code>
          </pre>
        );
      },
      table: ({ children, ...props }: any) => (
        <div className="my-4 overflow-x-auto">
          <table className={`w-full border-collapse text-left text-sm ${baseClass}`} {...props}>
            {children}
          </table>
        </div>
      ),
      thead: ({ children, ...props }: any) => (
        <thead className="bg-gray-100 dark:bg-gray-800" {...props}>
          {children}
        </thead>
      ),
      th: ({ children, ...props }: any) => (
        <th className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-900 dark:border-gray-700 dark:text-gray-100" {...props}>
          {children}
        </th>
      ),
      td: ({ children, ...props }: any) => (
        <td className="border-b border-gray-200 px-3 py-2 align-top text-gray-700 dark:border-gray-700 dark:text-gray-200" {...props}>
          {children}
        </td>
      ),
      hr: (props: any) => <hr className="my-6 border-dashed border-gray-300 dark:border-gray-700" {...props} />,
    };
  }, []);

  const noPodcasts = !podcastLoading && availablePodcasts.length === 0;
  const missingSelection = !podcastLoading && availablePodcasts.length > 0 && !selectedPodcast;

  if (podcastLoading) {
    return (
      <div className={cn("min-h-screen", theme.colors.backgroundCream, "flex flex-col")}>
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className="flex flex-col items-center gap-3 text-gray-700 dark:text-gray-200">
            <div className={cn("w-12 h-12 border-4 border-gray-200 dark:border-gray-700 border-t-transparent rounded-full animate-spin")} />
            <p className="text-lg font-medium">Loading podcasts…</p>
          </div>
        </main>
      </div>
    );
  }

  if (noPodcasts) {
    return (
      <div className={cn("min-h-screen flex flex-col", theme.colors.backgroundCream)}>
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className={cn("max-w-2xl w-full text-center space-y-6 px-8 py-10", theme.cards.base)}>
            <div
              className={cn(
                "mx-auto w-16 h-16 flex items-center justify-center rounded-full",
                theme.colors.primaryOrange,
                theme.borders.neubrutalism,
                theme.shadows.neubrutalismSm,
              )}
            >
              <span className="text-3xl">💬</span>
            </div>
            <div className="space-y-3">
              <h2 className="text-3xl font-black text-gray-900 dark:text-white">Add a podcast to start chatting</h2>
              <p className="text-lg text-gray-700 dark:text-gray-300">
                You need at least one podcast connected before you can ask questions. Add your first podcast to begin.
              </p>
            </div>
            <div className="flex justify-center">
              <Link
                to="/podcasts/add"
                className={cn(theme.buttons.primary, "inline-flex px-6 py-3")}
              >
                Add a podcast
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (missingSelection) {
    return (
      <div className={cn("min-h-screen flex flex-col", theme.colors.backgroundCream)}>
        <Header />
        <main className="flex-1 flex items-center justify-center px-4 py-16">
          <div className={cn("max-w-xl w-full text-center space-y-4 px-8 py-10", theme.cards.base)}>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Pick a podcast to chat</h2>
            <p className="text-gray-700 dark:text-gray-300">
              Select a podcast from the header to start your conversation.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.colors.backgroundCream} transition-colors duration-200 flex flex-col`}>
      <Header />

      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <span>Chat with</span>
              {selectedPodcast ? (
                <span
                  className={cn(
                    "inline-flex items-center gap-2 px-3 py-1 rounded-lg text-base sm:text-lg",
                    theme.colors.primaryOrange,
                    theme.borders.neubrutalism,
                    theme.shadows.neubrutalismSm,
                  )}
                >
                  <span className="text-xl">{selectedPodcast.emoji}</span>
                  <span>{selectedPodcast.name}</span>
                </span>
              ) : (
                <span>your podcast</span>
              )}
            </h2>
            {messages.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2 sm:gap-0">
                <div className="relative group">
                  <div className={`absolute inset-0 ${theme.colors.slate950} rounded-lg translate-y-1.5 translate-x-1.5 group-hover:translate-y-2 group-hover:translate-x-2 transition-transform`} />
                  <button
                    onClick={() => {
                      const formatted = messages
                        .map(msg => {
                          const sender = msg.role === "user" ? "You" : "AI Assistant";
                          const time = new Date(msg.timestamp).toLocaleTimeString();
                          const body = formatMessageForCopy(msg);
                          return `## ${sender} (${time})\n\n${body}\n\n`;
                        })
                        .join("---\n\n");
                      void navigator.clipboard.writeText(formatted);
                      setCopiedChat(true);
                      setTimeout(() => setCopiedChat(false), 2000);
                    }}
                    className={`relative z-10 w-full sm:w-auto px-3 sm:px-4 py-2 ${theme.buttons.secondary} rounded-lg font-medium text-sm sm:text-base hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-2`}
                  >
                    {copiedChat ? (
                      <>
                        <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="h-4 w-4" />
                        Copy Chat
                      </>
                    )}
                  </button>
                </div>

                <div className="relative group">
                  <div className={`absolute inset-0 ${theme.colors.slate950} rounded-lg translate-y-1.5 translate-x-1.5 group-hover:translate-y-2 group-hover:translate-x-2 transition-transform`} />
                  <button
                    onClick={clearChat}
                    className={`relative z-10 w-full sm:w-auto px-3 sm:px-4 py-2 ${theme.buttons.secondary} rounded-lg font-medium text-sm sm:text-base text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-gray-750 transition-colors flex items-center justify-center`}
                  >
                    Clear Chat
                  </button>
                </div>
              </div>
            )}
          </div>

          {!selectedPodcast && (
            <div className="bg-yellow-100 dark:bg-yellow-900 border-3 border-yellow-500 rounded-xl p-4 mb-8">
              <p className="text-yellow-700 dark:text-yellow-300">⚠️ Please select a podcast from the header to start chatting.</p>
            </div>
          )}

          {error && (
            <div className="bg-red-100 dark:bg-red-900 border-3 border-red-500 rounded-xl p-4 mb-8">
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          <div className="relative mb-6">
            <div className={`absolute inset-0 ${theme.colors.slate950} rounded-xl translate-y-2 translate-x-2`} />
            <div
              ref={chatContainerRef}
              className={`relative z-10 ${theme.cards.episode} p-4 min-h-[400px] max-h-[500px] md:max-h-[650px] lg:min-h-[500px] lg:max-h-[75vh] xl:max-h-[80vh] overflow-y-auto`}
            >
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                  <p className="text-center mb-2">Start chatting about the podcasts!</p>
                  <p className="text-center text-sm">Ask questions about episodes, topics, or historical figures.</p>
                  {selectedPodcast && (
                    <p className="text-center text-sm mt-2">
                      Chatting about: <strong>{selectedPodcast.name}</strong> {selectedPodcast.emoji}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
          {messages.map(message => (
                    <div key={message.id} className={message.role === "user" ? "ml-auto max-w-[85%]" : "mr-auto max-w-[85%]"}>
                      <div
                        className={`relative p-4 rounded-xl ${
                          message.role === "user"
                            ? "bg-blue-100 dark:bg-blue-900 border-3 border-blue-500 dark:border-blue-700"
                            : "bg-gray-100 dark:bg-gray-700 border-3 border-gray-900 dark:border-gray-600"
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2 gap-3">
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {message.role === "user" ? "You" : "AI Assistant"}
                          </div>
                          <button
                            onClick={() => {
                              void navigator.clipboard.writeText(formatMessageForCopy(message));
                              setCopiedMessageId(message.id);
                              setTimeout(() => setCopiedMessageId(null), 2000);
                            }}
                            className="text-xs text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-white transition-colors"
                          >
                            {copiedMessageId === message.id ? "✓ Copied" : "Copy"}
                          </button>
                        </div>

                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents as any}>
                          {message.content}
                        </ReactMarkdown>

                        <div className="mt-3 text-xs text-gray-500 dark:text-gray-300">
                          <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
                          {message.verdict && (
                            <span className="ml-2 font-semibold">
                              {message.verdict === "answer" ? "Answer provided" : "Insufficient context"}
                            </span>
                          )}
                          {message.usedContext && <span className="ml-2">{formatUsedContext(message.usedContext)}</span>}
                          {typeof message.chunksUsed === "number" && <span className="ml-2">Chunks: {message.chunksUsed}</span>}
                          {message.model && <span className="ml-2">Model: {message.model}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="mr-auto max-w-[85%]">
                      <div className={`relative p-4 rounded-xl bg-gray-100 dark:bg-gray-700 ${theme.borders.neubrutalism}`}>
                        <div className="flex space-x-2">
                          <span className="h-2.5 w-2.5 rounded-full bg-gray-500 dark:bg-gray-300 animate-[chat-bounce_1.2s_infinite]" style={{ animationDelay: "0ms" }} />
                          <span className="h-2.5 w-2.5 rounded-full bg-gray-500 dark:bg-gray-300 animate-[chat-bounce_1.2s_infinite]" style={{ animationDelay: "150ms" }} />
                          <span className="h-2.5 w-2.5 rounded-full bg-gray-500 dark:bg-gray-300 animate-[chat-bounce_1.2s_infinite]" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="relative">
            <div className={`absolute inset-0 ${theme.colors.slate950} rounded-xl translate-y-2 translate-x-2`} />
            <div className={`relative z-10 bg-white dark:bg-gray-800 ${theme.borders.slate950} rounded-xl p-4`}>
              <textarea
                value={inputMessage}
                onChange={event => setInputMessage(event.target.value)}
                onKeyDown={event => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void sendMessage();
                  }
                }}
                placeholder="Ask about episodes, topics, or people..."
                className={`w-full min-h-[120px] bg-transparent border-2 border-gray-200 dark:border-gray-600 rounded-xl p-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 ${theme.ring.primaryOrange}`}
                disabled={!selectedPodcast}
              />
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-gray-500 dark:text-gray-300">
                  Shift+Enter for newline. We reuse previous context automatically.
                </div>
                <button
                  onClick={() => void sendMessage()}
                  disabled={isLoading || !inputMessage.trim() || !selectedPodcast}
                  className={`inline-flex items-center justify-center px-5 py-2 rounded-xl ${theme.buttons.primary} focus:outline-none focus:ring-4 focus:ring-primary-orange/40 dark:focus:ring-primary-orange-dark/30 disabled:cursor-not-allowed disabled:opacity-75`}
                >
                  {isLoading ? "Thinking…" : "Send"}
                </button>
              </div>
            </div>
          </div>

          {Object.keys(episodeReferenceCounts).length > 0 && (
            <section className="mt-8">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Episodes referenced in this chat
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Sorted by reference count and publication date.
                </p>
              </div>

              {episodesLoading ? (
                <div className="rounded-xl border-3 border-dashed border-gray-300 dark:border-gray-600 px-4 py-6 text-center text-sm text-gray-600 dark:text-gray-300">
                  Loading referenced episodes…
                </div>
              ) : referencedEpisodes.length === 0 ? (
                <div className="rounded-xl border-3 border-dashed border-gray-300 dark:border-gray-600 px-4 py-6 text-center text-sm text-gray-600 dark:text-gray-300">
                  We couldn't match the referenced episodes yet. Ask another question to refresh the list.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                  {referencedEpisodes.map(({ episode, count }) => (
                    <div key={episode.guid} className="flex flex-col">
                      <EpisodeCard episode={episode} onClick={() => setSelectedEpisode(episode)} />
                      <p className="mt-3 text-center text-sm font-medium text-gray-600 dark:text-gray-300">
                        Referenced {count} {count === 1 ? "time" : "times"}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {selectedEpisode && <EpisodeModal episode={selectedEpisode} onClose={() => setSelectedEpisode(null)} />}
        </div>
      </main>
    </div>
  );
}
